import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Financial Aid",
  "lead": "Financial Aid Lead"
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_FINANCE"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Aid to education: time for tough decisions and clearer data in times of crisis."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Aid to education is particularly important in low-income counties where it accounts for 12% of total education spending, or 17%, if household education spending is excluded."
    }), "\n", _jsx(_components.h2, {
      id: "aid-trends",
      children: "Aid trends"
    }), "\n", _jsxs(_components.p, {
      children: ["External financing plays a key role in supporting education in the poorest countries. Between 2023 and 2030, the annual SDG 4 financing gap for low- and lower-middle-income countries is estimated at ", _jsx(_components.a, {
        href: "https://www.unesco.org/gem-report/en/can-countries-afford-sdg4-benchmarks",
        children: "USD 97 billion"
      }), ". If all 33 DAC countries had met the ", _jsx(_components.a, {
        href: "https://web-archive.oecd.org/pageViewer?path=/2024-06-17/63452-the07odagnitarget-ahistory.htm&title=The%200.7%25%20ODA/GNI%20target%20-%20a%20history",
        children: "0.7% ODA/GNI target"
      }), " and prioritised education, USD 29 billion more could have been mobilised each year. Yet ODA remained at 0.33% in 2024, a level unchanged since 2005. Only Denmark, Luxembourg, Norway and Sweden met the target. Since 2015, more aid has been redirected to refugee costs and, since 2022, to Ukraine. Amid declining public support, shifting political priorities, and scrutiny of aid effectiveness, development assistance is entering a period of uncertainty. Education may be particularly at risk as several donors reduce future ODA commitments."]
    }), "\n", _jsx(Figure, {
      id: "ODA_GDP"
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(HighlightByIndex, {
        index: "0",
        children: "share of education in total aid"
      }), " rose slightly in 2023 to 8.4%, recovering from a low of 7.6% in 2022, but remains below its 2010 level of 9.9%. Despite ongoing advocacy, the long-term trend shows a gradual decline. By contrast, the ", _jsx(HighlightByIndex, {
        index: "3",
        children: "share of governance in total aid"
      }), " has steadily increased, reaching 20.2% in 2023 — its highest level in over a decade."]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "SECTOR_ALLOCABLE"
        })
      }), _jsx(ScrollySection, {
        id: "Health",
        children: _jsxs(_components.p, {
          children: ["As a result of the COVID-19 pandemic, the", " ", "\n", _jsx(HighlightByIndex, {
            index: "1",
            children: "share of health in total aid"
          }), " ", "\nincreased sharply, reaching an all-time high of 24.3% in 2022. It declined to 17.9% in 2023 as emergency support tapered off."]
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "Total direct aid to education amounted to USD 12.7 billion in 2023 and has remained essentially constant since 2016. Several donors have since announced cuts. The United Kingdom reduced its aid share to 0.5 percent in 2021 and plans to lower it further to 0.3 percent by 2027. Germany, France, Switzerland, the Netherlands and Belgium have also signaled significant reductions since 2023. The United States suspended 83 percent of its education aid in early 2025. These reductions are expected to drive a sharp decline: aid to education is projected to fall by a quarter between 2023 and 2027. Analysis of International Aid Transparency Initiative data suggests a 12 percent drop may have already occurred in 2024, with at least a further 14 percent decline by 2027. Low-income countries are likely to be hardest hit, with some projected to lose up to half of current aid levels."
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "EDU_SUB"
        })
      }), _jsx(ScrollySection, {
        id: "absolute,2024,2025,2026,2027",
        children: _jsx(_components.p, {
          children: "Some countries stand to lose a lot due to the ODA cuts. In countries like Niger, Rwanda, and Sierra Leone, aid represents around 20% of total education budgets."
        })
      }), _jsx(ScrollySection, {
        id: "relative",
        children: _jsxs(_components.p, {
          children: ["The share of aid to ", _jsx(HighlightByIndex, {
            index: "0",
            children: "basic education"
          }), " has been slowly declining (from 40% in 2016 to 30% in 2023), losing ground equally to ", _jsx(HighlightByIndex, {
            index: "1",
            children: "secondary"
          }), " and ", _jsx(HighlightByIndex, {
            index: "2",
            children: "post-secondary education"
          }), "."]
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "largest-donors",
      children: "Largest donors"
    }), "\n", _jsx(_components.p, {
      children: "The top ten donors disbursed USD 7.7 billion per year between 2010 and 2023, or 76% of country programmable aid to education. IDA led with USD 1.6 billion annually, followed by the United States, Germany, the EU, and the United Kingdom."
    }), "\n", _jsx(_components.p, {
      children: "Bilateral donors channel only 17% of aid through recipient governments, compared to multilateral donors who channel 60%. So pooled funds, such as the Global Partnership for Education (GPE) and Education Cannot Wait (ECW), play a central role in education."
    }), "\n", _jsx(_components.p, {
      children: "The GPE acts as an intermediary fund, disbursing resources from bilateral donors to country-level partners. Its spending can be tracked through three sources: the OECD CRS database, which documents disbursements from bilateral donors to the GPE; the International Aid Transparency Initiative, and GPE’s own financial reporting, which captures grant utilization. Between 2011 and 2023, CRS data show average annual disbursements to GPE of USD 471 million, while GPE reports average utilization of USD 419 million. GPE accounted for 11–12% of direct aid to basic education during this period, which averaged USD 3.9 billion annually."
    }), "\n", _jsx(Figure, {
      id: "GPE"
    }), "\n", _jsx(_components.p, {
      children: "ECW has consistently published data to IATI. Reported disbursements, expressed in 2022 constant USD, increased from USD 57 million in 2017 to USD 167 million in 2020 and USD 190 million in 2024. Donor-reported contributions in the CRS follow a broadly similar trend, though not identical."
    }), "\n", _jsx(_components.h2, {
      id: "equity",
      children: "Equity"
    }), "\n", _jsx(_components.p, {
      children: "Volume of aid is not the only important consideration. It also matters how effective aid is and how equitably it is allocated. There are several ways to look at equity in aid."
    }), "\n", _jsx(IndicatorReference, {
      id: "4.5.5",
      children: _jsx(_components.p, {
        children: "First, the share of total aid going to education is declining. Between 2011 and 2020, it dropped from 14% to 8% for the World Bank, from 11% to 8% for France, from 12% to 8% for Germany, and from 10% to 5% for Japan. Only the United States saw a small increase, from 5% to 8%, but this remained below the global average. In 2023, France and the World Bank saw a slight recovery, reaching around 11–12%. The only major donor to steadily increase its share was the European Union, rising from 5.6% in 2014 to 10.2% in 2023, in line with its policy."
      })
    }), "\n", _jsx(_components.p, {
      children: "Second, considering that the poorest children are more likely not to even complete primary school, the share of aid allocated to basic education is another indication of equity orientation."
    }), "\n", _jsx(_components.p, {
      children: "The share of basic education in total direct aid fell from 40% in 2016 to 30% in 2023, as other levels gained ground. The United States reduced its share from 82% in 2018 to 63% in 2023, with more aid marked as unspecified. Most donors saw little change or decline, except Japan, where the share rose from 7% in 2011 to 18% in 2023, though it remains low."
    }), "\n", _jsx(_components.p, {
      children: "Third, aid should prioritise countries most in need. Yet from 2010 to 2023, lower-middle-income countries received the largest share (45%), while low-income, upper-middle-income, and unspecified groups each received just under 20%. Donors vary: the World Bank prioritised lower-middle-income countries (67%), UNRWA upper-middle-income countries (65%), and the UK unspecified income groups (47%)."
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "AID_TO_EDU"
        })
      }), _jsx(ScrollySection, {
        id: "GBR,0",
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(HighlightByIndex, {
            index: "0",
            children: "United Kingdom"
          }), " allocates\n9% of its total aid to education..."]
        })
      }), _jsx(ScrollySection, {
        id: "GBR,1",
        children: _jsxs(_components.p, {
          children: ["...but the share of aid to education that the", " ", "\n", _jsx(HighlightByIndex, {
            index: "0",
            children: "United Kingdom"
          }), " is spending on\nbasic education has fallen from a peak of 62% in 2016 to 41% in 2023 due to its shift in increasing allocation to other education levels."]
        })
      }), _jsx(ScrollySection, {
        id: "GBR,2",
        children: _jsxs(_components.p, {
          children: ["The share of total direct aid to education that the", " ", "\n", _jsx(HighlightByIndex, {
            index: "0",
            children: "United Kingdom"
          }), " allocates to\nlow-income countries fell from 35% in 2010 to 12% in 2016, but gradually rose to 17% in 2021, 21% in 2022, and 25% in 2023 as more funding went to global public goods in education."]
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "sub-saharan-africa",
      children: "Sub-Saharan Africa"
    }), "\n", _jsx(_components.p, {
      children: "Comparing regions, the largest recipient of aid to education in 2023 was sub-Saharan Africa (US$4.1 billion), followed by Northern Africa and Western Asia (US$2.5 billion). In total, around a quarter of donors' aid to basic education is allocated to sub-Saharan Africa, home to 55% of the world's out of school children. In terms of regional distribution, there has been a clear shift of aid to education in recent years to sub-Saharan Africa and the Northern Africa and Western Asia. These two regions accounted for 39% of direct aid to education in 2010 and 54% in 2023."
    }), "\n", _jsx(_components.p, {
      children: "The World Bank is the largest donor to education in sub-Saharan Africa. Its support, which fluctuated around US$500 million in the 2000s, more than tripled from US$379 million in 2015 to US$1.1 billion in 2021 and further increased to US$1.9 billion in 2022. The increase was particularly significant in basic education, rising from US$490 million in 2021 to US$950 million in 2022. The United Kingdom has reduced their direct funding to the region, but this is because it has shifted funds to support the Global Partnership for Education, which focuses on low-income countries and in particular on Sub-Saharan Africa."
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "AID_EDU_SSA"
        })
      }), _jsx(ScrollySection, {
        id: "USA,FRA",
        children: _jsxs(_components.p, {
          children: [_jsx(HighlightByIndex, {
            index: "0",
            children: "The United States of America"
          }), " and", " ", "\n", _jsx(HighlightByIndex, {
            index: "1",
            children: "France"
          }), " are currently the\nsecond and third largest donors to the region. The United States primarily focused its spending on basic education, while France allocated about 40% of its aid to this sector."]
        })
      }), _jsx(ScrollySection, {
        id: "World Bank (IDA)",
        children: _jsxs(_components.p, {
          children: ["The increase in the", " ", "\n", _jsx(HighlightByIndex, {
            index: "2",
            children: "World Bank (IDA)'s"
          }), " aid to\nsub-Saharan Africa since 2019 is mostly driven by aid to two countries:\nRwanda and the Democratic Republic of the Congo."]
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "This flow diagram below of the 10 largest donors to sub-Saharan Africa shows that only 30% of direct aid to education goes straight to recipient countries, with the remainder going through donors’ own aid agencies, international and domestic NGOs and multilateral organizations, including GPE and UNICEF. Of the 10 largest donors, only the World Bank channels funds directly to recipient countries while bilateral donors such as the European Union, Germany, the United Kingdom and the United States allocate no more than 20% of aid to recipient countries. As a result, one third of the aid from the top 10 donors goes to unspecified recipient countries."
    }), "\n", _jsx(Figure, {
      id: "AID_FLOWS_GROUPED"
    }), "\n", _jsx(_components.h2, {
      id: "funding-for-education-in-crises",
      children: "Funding for education in crises"
    }), "\n", _jsx(_components.p, {
      children: "Armed conflict, climate shocks and displacement continue to disrupt education, with 122.6 million people forcibly displaced by mid-2024. Education in emergencies and protracted crises (EiEPC) plays a vital role in providing access to learning, protection and essential services, but remains severely underfunded, with only 29% of funding requests met. Donors support EiEPC through both humanitarian and development channels, yet financing remains fragmented across systems like OECD CRS, OCHA FTS and IATI, limiting coordination and strategic planning."
    }), "\n", _jsx(_components.p, {
      children: "CRS and FTS data suggest that EiEPC financing increased rapidly by some USD 700 million in five years: in the case of the CRS, from USD 967 million in 2016 to USD 1.7 billion in 2021; and in the case of the FTS, from USD 564 million in 2016 to almost USD 1.33 billion in 2022. In both cases, it fell by about 20% in the following two years. At the same time, the CRS and the FTS differ considerably in the data they report. CRS aid volumes are one third higher than FTS aid volumes on average. The difference in volume reflects their distinct scopes and methodologies."
    }), "\n", _jsx(Figure, {
      id: "CRS"
    }), "\n", _jsx(_components.p, {
      children: "The share of EiEPC financed through humanitarian channels has declined steadily in the CRS database from 30% financing in 2017 to just 8% in 2023, the remainder coming from development aid channels. In terms of total aid to education, the share of EiEPC has increased from 9% in 2017 to 12% in 2023. When focusing only on countries with humanitarian response plans in place for more than three years, the share rose from 21% to 24% in this period."
    }), "\n", _jsx(Figure, {
      id: "EDU_SCHOL_TT"
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsxs(_components.p, {
      children: ["Aid is an important source of education financing in low-income countries, which must be analysed jointly with ", _jsx(_components.a, {
        href: "https://www.education-progress.org/en/articles/finance",
        children: "domestic public and private funding"
      }), ". In addition to increasing the funds available for education, equitable distribution needs to be strengthened. In order to achieve the global education goal, SDG 4, it is important that countries and donors take responsibility and fulfil their commitments."]
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000394153",
            children: "Aid to education: time for tough decisions"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000394154",
            children: "Funding for education in crises: data in distress"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/2024efw",
            children: "Education Finance Watch 2024"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
